import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { PairingService } from "../services/pairingService";
import { aiPairingService } from "../services/aiPairingService";
import "./PairingInterface.css";

/**
 * PairingInterface Component - Phase 2.2 Implementation
 *
 * Interactive pairing suggestions interface that displays AI-powered
 * food-beverage pairings with confidence scores and explanations.
 */
const PairingInterface = ({
  beverage,
  restaurantId,
  menuItems = [],
  compact = false,
  onPairingSelect = null,
}) => {
  const [pairings, setPairings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPairing, setSelectedPairing] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);

  const pairingService = useMemo(() => new PairingService(), []);

  const loadPairings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get AI-generated pairings for this beverage
      const aiPairings = await aiPairingService.generateMenuPairings(
        restaurantId,
        beverage.id,
        {
          includeGeneric: menuItems.length === 0,
          maxPairings: compact ? 3 : 8,
          confidenceThreshold: 0.6,
        }
      );

      // Enhanced with science-based pairing analysis
      const enhancedPairings = await Promise.all(
        aiPairings.map(async (pairing) => {
          const analysis = pairingService.analyzePairing(
            beverage,
            pairing.foodItem || pairing,
            {},
            { occasion: "dinner" }
          );

          return {
            ...pairing,
            scienceScore: analysis.score,
            scienceExplanation: analysis.explanation,
            factors: analysis.factors,
            rationale: analysis.rationale,
            colorClass: analysis.colorClass,
          };
        })
      );

      // Sort by combined AI confidence and science score
      const sortedPairings = enhancedPairings.sort((a, b) => {
        const scoreA = a.confidence * 0.6 + a.scienceScore * 0.4;
        const scoreB = b.confidence * 0.6 + b.scienceScore * 0.4;
        return scoreB - scoreA;
      });

      setPairings(sortedPairings);
    } catch (err) {
      console.error("Error loading pairings:", err);
      setError("Failed to load pairing suggestions");
    } finally {
      setLoading(false);
    }
  }, [beverage, restaurantId, menuItems, compact, pairingService]);

  useEffect(() => {
    if (beverage && restaurantId) {
      loadPairings();
    }
  }, [beverage, restaurantId, loadPairings]);

  const handlePairingClick = (pairing) => {
    setSelectedPairing(pairing);
    setShowExplanation(true);

    if (onPairingSelect) {
      onPairingSelect(pairing);
    }
  };

  const getConfidenceLevel = (confidence) => {
    if (confidence >= 0.9) return "Excellent";
    if (confidence >= 0.8) return "Very Good";
    if (confidence >= 0.7) return "Good";
    if (confidence >= 0.6) return "Fair";
    return "Moderate";
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return "#22c55e";
    if (confidence >= 0.8) return "#84cc16";
    if (confidence >= 0.7) return "#eab308";
    if (confidence >= 0.6) return "#f97316";
    return "#ef4444";
  };

  if (loading) {
    return (
      <div className={`pairing-interface ${compact ? "compact" : ""}`}>
        <div className="pairing-header">
          <h3>🍽️ Perfect Pairings</h3>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Finding perfect pairings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`pairing-interface ${compact ? "compact" : ""}`}>
        <div className="pairing-header">
          <h3>🍽️ Perfect Pairings</h3>
        </div>
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button onClick={loadPairings} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`pairing-interface ${compact ? "compact" : ""}`}>
      <div className="pairing-header">
        <h3>🍽️ Perfect Pairings</h3>
        {!compact && (
          <p className="pairing-subtitle">
            AI-curated food pairings that complement {beverage.name}
          </p>
        )}
      </div>

      <div className="pairings-grid">
        {pairings.map((pairing, index) => (
          <button
            key={`${pairing.foodName || pairing.name}-${
              pairing.confidence
            }-${index}`}
            className="pairing-card"
            onClick={() => handlePairingClick(pairing)}
            type="button"
          >
            <div className="pairing-info">
              <h4 className="pairing-name">
                {pairing.foodName || pairing.name}
              </h4>

              {pairing.category && (
                <span className="pairing-category">{pairing.category}</span>
              )}

              <p className="pairing-description">
                {pairing.description || pairing.reason}
              </p>
            </div>

            <div className="pairing-metrics">
              <div className="confidence-score">
                <div
                  className="confidence-bar"
                  style={{
                    width: `${pairing.confidence * 100}%`,
                    backgroundColor: getConfidenceColor(pairing.confidence),
                  }}
                />
                <span className="confidence-text">
                  {getConfidenceLevel(pairing.confidence)}
                </span>
              </div>

              {pairing.scienceScore !== undefined && (
                <div className={`science-score ${pairing.colorClass || ""}`}>
                  <span className="score-label">Science Score:</span>
                  <span className="score-value">
                    {pairing.scienceScore.toFixed(0)}
                  </span>
                </div>
              )}
            </div>

            <div className="pairing-tags">
              {pairing.tags?.slice(0, 3).map((tag, tagIndex) => (
                <span key={tagIndex} className="pairing-tag">
                  {tag}
                </span>
              ))}
            </div>
          </button>
        ))}
      </div>

      {pairings.length === 0 && (
        <div className="no-pairings">
          <p>No pairings available for this beverage yet.</p>
          <button onClick={loadPairings} className="generate-button">
            Generate Pairings
          </button>
        </div>
      )}

      {/* Pairing Explanation Modal */}
      {showExplanation && selectedPairing && (
        <button
          className="pairing-modal-overlay"
          type="button"
          onClick={() => setShowExplanation(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowExplanation(false);
            }
          }}
          aria-label="Close modal"
        >
          <div className="pairing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Why This Pairing Works</h3>
              <button
                className="close-button"
                onClick={() => setShowExplanation(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="pairing-hero">
                <h4>{selectedPairing.foodName || selectedPairing.name}</h4>
                <p>
                  paired with <strong>{beverage.name}</strong>
                </p>
              </div>

              <div className="explanation-sections">
                <div className="ai-explanation">
                  <h5>🤖 AI Analysis</h5>
                  <p>{selectedPairing.description || selectedPairing.reason}</p>
                  <div className="confidence-detail">
                    <span>Confidence: </span>
                    <strong
                      style={{
                        color: getConfidenceColor(selectedPairing.confidence),
                      }}
                    >
                      {getConfidenceLevel(selectedPairing.confidence)} (
                      {(selectedPairing.confidence * 100).toFixed(0)}%)
                    </strong>
                  </div>
                </div>

                {selectedPairing.scienceExplanation && (
                  <div className="science-explanation">
                    <h5>🧪 Science-Based Analysis</h5>
                    <p>{selectedPairing.scienceExplanation}</p>
                    {selectedPairing.rationale && (
                      <div className="rationale-breakdown">
                        <h6>Detailed Breakdown:</h6>
                        <ul>
                          {selectedPairing.rationale.map((item, index) => (
                            <li key={index} className="rationale-item">
                              <span
                                className={`points ${
                                  item.points?.startsWith("+")
                                    ? "positive"
                                    : "negative"
                                }`}
                              >
                                {item.points}
                              </span>
                              <span className="reason">{item.reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {selectedPairing.factors && (
                  <div className="pairing-factors">
                    <h5>🎯 Key Factors</h5>
                    <div className="factors-grid">
                      {Object.entries(selectedPairing.factors).map(
                        ([factor, value]) => (
                          <div key={factor} className="factor-item">
                            <span className="factor-name">{factor}:</span>
                            <span className="factor-value">{value}</span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </button>
      )}
    </div>
  );
};

PairingInterface.propTypes = {
  beverage: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string,
    category: PropTypes.string,
  }).isRequired,
  restaurantId: PropTypes.string.isRequired,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      category: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  compact: PropTypes.bool,
  onPairingSelect: PropTypes.func,
};

PairingInterface.defaultProps = {
  menuItems: [],
  compact: false,
  onPairingSelect: null,
};

export default PairingInterface;
